import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getActiveProjectId, setActiveProjectId } from "../lib/api";
import { useAuth } from "./AuthContext";

const ProjectContext = createContext(null);

export function ProjectProvider({ children }) {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [activeId, setActiveId] = useState(getActiveProjectId());
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // Auth states: null = checking (keep loading), false = unauthed (stop), object = authed
    if (user === null) return;
    if (user === false) { setLoading(false); setProjects([]); return; }
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
      const live = data.filter((p) => p.status !== "archived");
      let next = activeId && live.find((p) => p.id === activeId) ? activeId : (live[0]?.id || null);
      if (next !== activeId) {
        setActiveId(next);
        setActiveProjectId(next);
      }
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const switchProject = useCallback(async (id) => {
    setActiveId(id);
    setActiveProjectId(id);
    try {
      if (id) await api.post(`/projects/active/${id}`);
    } catch {
      /* ignore */
    }
  }, []);

  const createProject = async (form) => {
    // Pass the whole form (incl. optional `template`) so new fields propagate
    // without each one requiring a destructure change here.
    const { data } = await api.post("/projects", form);
    await refresh();
    await switchProject(data.id);
    return data;
  };

  const activeProject = projects.find((p) => p.id === activeId) || null;

  return (
    <ProjectContext.Provider value={{ projects, activeProject, activeId, loading, refresh, switchProject, createProject, setActiveId: switchProject }}>
      {children}
    </ProjectContext.Provider>
  );
}

export const useProject = () => useContext(ProjectContext);
